import React, { useState, useEffect, useCallback } from 'react';
import { primaryButtonSx, pageTabsSx } from '../../styles/adminPageStyles';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Button,
  useTheme,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Tooltip,
  Stack,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Build as BuildIcon,
  HourglassEmpty as HourglassEmptyIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import CheckResponses from './CheckResponses';
import PageHeader from '../../components/PageHeader';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { useSearchParams } from 'react-router-dom';
import { useSiteTerminology } from '../../hooks/useSiteTerminology';
import { locationLabel } from '../../utils/terminology';
import { resolveAdminUiLabel } from '../../constants/adminUiLabels';
import { useAdminUiLabels } from '../../context/AdminUiLabelsContext';

// ─── Types ─────────────────────────────────────────────────────────────────────

type RemedialStatus = 'open' | 'in_progress' | 'awaiting_verification';

interface MediaItem {
  url: string;
  type: 'image' | 'video';
  mime_type: string;
  thumbnail_url?: string;
}

interface RemedialUpdate {
  update_id: string;
  note: string;
  status_change: RemedialStatus | 'resolved' | null;
  media_urls: MediaItem[] | null;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`location-checks-tabpanel-${index}`}
      aria-labelledby={`location-checks-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface StatisticsCardProps {
  title: string;
  count: React.ReactNode;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
}

const StatisticsCard: React.FC<StatisticsCardProps> = ({ title, count, icon, color }) => {
  const theme = useTheme();
  const colorValue = theme.palette[color].main;
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 4,
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 20px 45px rgba(0,0,0,0.12)' },
      }}
    >
      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, '&:last-child': { pb: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48, height: 48, borderRadius: '16px',
              backgroundColor: `${colorValue}22`, color: colorValue,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {React.cloneElement(icon as React.ReactElement, { fontSize: 'large' })}
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">{title}</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>{count}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

interface RecentCheck {
  safety_check_id: string;
  check_name: string;
  location_name: string | null;
  safety_check_status: string;
  assigned_to_name?: string | null;
  completed_at?: string | null;
  analysis_excerpt?: string | null;
  analysis_full?: string | null;
}

interface LocationAuditLog {
  audit_id: string;
  location_id: string;
  location_name: string;
  action: 'marked_out_of_action' | 'marked_safe';
  note: string | null;
  safety_check_id: string | null;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  is_out_of_action?: number;
  check_type?: string | null;
  remedial_status?: RemedialStatus | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

const STATUS_META: Record<RemedialStatus, { label: string; color: 'error' | 'warning' | 'info'; icon: React.ReactNode }> = {
  open: { label: 'Open', color: 'error', icon: <WarningIcon fontSize="small" /> },
  in_progress: { label: 'In Progress', color: 'warning', icon: <BuildIcon fontSize="small" /> },
  awaiting_verification: { label: 'Awaiting Verification', color: 'info', icon: <HourglassEmptyIcon fontSize="small" /> },
};

const effectiveStatus = (log: LocationAuditLog): RemedialStatus =>
  (log.remedial_status as RemedialStatus) ?? 'open';

const LOCATION_CHECK_TABS = ['overview', 'responses'] as const;

const tabSlugToIndex = (tabParam: string | null, safetyCheckIdParam: string | null): number => {
  if (safetyCheckIdParam || tabParam === 'responses') return 1;
  return 0;
};

const isFailedStatus = (status: string | null | undefined) => {
  const s = (status || '').toLowerCase();
  return s === 'fail' || s === 'failed';
};

const getAnalysisExcerpt = (text: string, maxLen = 220): string => {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  const body = (lines[0] === 'PASS' || lines[0] === 'FAIL' ? lines.slice(1) : lines).join(' ').trim();
  if (!body) return '';
  return body.length > maxLen ? `${body.slice(0, maxLen)}…` : body;
};

// ─── Remedial Updates Dialog ────────────────────────────────────────────────────

interface RemedialUpdatesDialogProps {
  open: boolean;
  log: LocationAuditLog | null;
  locationLabel: string;
  onClose: () => void;
  onResolved: () => void;
}

const RemedialUpdatesDialog: React.FC<RemedialUpdatesDialogProps> = ({
  open,
  log,
  locationLabel: locLabel,
  onClose,
  onResolved,
}) => {
  const token = localStorage.getItem('token');
  const [updates, setUpdates] = useState<RemedialUpdate[]>([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);

  // Add update form
  const [note, setNote] = useState('');
  const [statusChange, setStatusChange] = useState<RemedialStatus | null>(null);
  const [mediaFiles, setMediaFiles] = useState<Array<{ file: File; uploading: boolean; url: string; type: 'image' | 'video' }>>([]);
  const [submitting, setSubmitting] = useState(false);

  // Mark resolved form
  const [showResolveForm, setShowResolveForm] = useState(false);
  const [resolveNote, setResolveNote] = useState('');
  const [resolveMedia, setResolveMedia] = useState<Array<{ file: File; uploading: boolean; url: string; type: 'image' | 'video' }>>([]);
  const [resolving, setResolving] = useState(false);

  // Lightbox
  const [lightbox, setLightbox] = useState<{ open: boolean; url: string; type: string }>({ open: false, url: '', type: '' });

  const fetchUpdates = useCallback(async () => {
    if (!log) return;
    setUpdatesLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/locations/${log.location_id}/remedial-updates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) setUpdates(res.data.data || []);
    } catch { /* silently fail */ }
    finally { setUpdatesLoading(false); }
  }, [log, token]);

  useEffect(() => {
    if (open && log) { fetchUpdates(); }
    if (!open) {
      setUpdates([]);
      setNote('');
      setStatusChange(null);
      setMediaFiles([]);
      setShowResolveForm(false);
      setResolveNote('');
      setResolveMedia([]);
    }
  }, [open, log, fetchUpdates]);

  const uploadMedia = async (
    file: File,
    locationId: string,
    setter: React.Dispatch<React.SetStateAction<Array<{ file: File; uploading: boolean; url: string; type: 'image' | 'video' }>>>,
  ) => {
    const formData = new FormData();
    formData.append('media', file);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/locations/${locationId}/remedial-updates/upload-media`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } },
      );
      if (res.data?.success) {
        setter((prev) =>
          prev.map((m) => m.file === file ? { ...m, uploading: false, url: res.data.data.url } : m),
        );
      }
    } catch {
      setter((prev) => prev.map((m) => m.file === file ? { ...m, uploading: false } : m));
    }
  };

  const handleFileSelect = async (
    files: FileList | null,
    setter: React.Dispatch<React.SetStateAction<Array<{ file: File; uploading: boolean; url: string; type: 'image' | 'video' }>>>,
    locationId: string,
  ) => {
    if (!files) return;
    const newItems = Array.from(files).map((file) => ({
      file,
      uploading: true,
      url: '',
      type: (file.type.startsWith('video/') ? 'video' : 'image') as 'image' | 'video',
    }));
    setter((prev) => [...prev, ...newItems]);
    for (const item of newItems) {
      await uploadMedia(item.file, locationId, setter);
    }
  };

  const handleSubmitUpdate = async () => {
    if (!log || !note.trim()) return;
    const pendingUploads = mediaFiles.filter((m) => m.uploading);
    if (pendingUploads.length > 0) { alert('Please wait for media uploads to complete.'); return; }

    setSubmitting(true);
    try {
      const mediaPayload = mediaFiles.filter((m) => m.url).map(({ url, type, file }) => ({ url, type, mime_type: file.type }));
      await axios.post(
        `${API_BASE_URL}/locations/${log.location_id}/remedial-updates`,
        {
          note: note.trim(),
          status_change: statusChange || null,
          media_urls: mediaPayload.length > 0 ? mediaPayload : null,
          original_audit_id: log.audit_id,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setNote('');
      setStatusChange(null);
      setMediaFiles([]);
      await fetchUpdates();
    } catch { alert('Failed to add update. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const handleResolve = async () => {
    if (!log) return;
    const pendingUploads = resolveMedia.filter((m) => m.uploading);
    if (pendingUploads.length > 0) { alert('Please wait for media uploads to complete.'); return; }

    setResolving(true);
    try {
      const mediaPayload = resolveMedia.filter((m) => m.url).map(({ url, type, file }) => ({ url, type, mime_type: file.type }));

      if (resolveNote.trim() || mediaPayload.length > 0) {
        await axios.post(
          `${API_BASE_URL}/locations/${log.location_id}/remedial-updates`,
          {
            note: resolveNote.trim() || 'Issue resolved.',
            status_change: 'resolved',
            media_urls: mediaPayload.length > 0 ? mediaPayload : null,
            original_audit_id: log.audit_id,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      await axios.put(
        `${API_BASE_URL}/locations/${log.location_id}/out-of-action`,
        {
          is_out_of_action: false,
          out_of_action_note: resolveNote.trim() || null,
          related_safety_check_id: log.safety_check_id,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onResolved();
      onClose();
    } catch { alert('Failed to resolve. Please try again.'); }
    finally { setResolving(false); }
  };

  if (!log) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Remedial Actions</Typography>
            <Typography variant="body2" color="text.secondary">{log.location_name}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={STATUS_META[effectiveStatus(log)].icon}
              label={STATUS_META[effectiveStatus(log)].label}
              color={STATUS_META[effectiveStatus(log)].color}
              size="small"
              variant="outlined"
            />
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {/* Issue origin */}
          <Box sx={{ px: 3, py: 2, bgcolor: 'error.50', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="error.main" fontWeight={700} textTransform="uppercase" letterSpacing={0.8}>
              Issue opened {new Date(log.created_at).toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{log.note || 'Failed site check'}</Typography>
          </Box>

          {/* Updates timeline */}
          <Box sx={{ px: 3, py: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Follow-up history
            </Typography>
            {updatesLoading ? (
              <LinearProgress sx={{ mb: 2 }} />
            ) : updates.length === 0 ? (
              <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mb: 2 }}>
                No follow-ups yet. Add the first update below.
              </Typography>
            ) : (
              <Stack spacing={0} sx={{ mb: 2 }}>
                {updates.map((upd, idx) => {
                  const mediaItems: MediaItem[] = Array.isArray(upd.media_urls) ? upd.media_urls : [];
                  return (
                    <Box key={upd.update_id} sx={{ display: 'flex', gap: 1.5 }}>
                      {/* Timeline spine */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 24 }}>
                        <Box sx={{
                          width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.75, flexShrink: 0,
                          ...(upd.status_change === 'resolved' && { bgcolor: 'success.main' }),
                        }} />
                        {idx < updates.length - 1 && (
                          <Box sx={{ flex: 1, width: 2, bgcolor: 'divider', my: 0.5 }} />
                        )}
                      </Box>
                      <Box sx={{ flex: 1, pb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Typography variant="caption" fontWeight={700}>
                            {[upd.first_name, upd.last_name].filter(Boolean).join(' ') || 'User'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(upd.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                        {upd.status_change && (
                          <Chip
                            size="small"
                            label={upd.status_change === 'resolved' ? 'Resolved' :
                              upd.status_change === 'in_progress' ? 'Marked In Progress' :
                              'Awaiting Verification'}
                            color={upd.status_change === 'resolved' ? 'success' :
                              upd.status_change === 'in_progress' ? 'warning' : 'info'}
                            variant="outlined"
                            sx={{ mb: 0.5, fontSize: '0.7rem' }}
                          />
                        )}
                        <Typography variant="body2" sx={{ mt: 0.25 }}>{upd.note}</Typography>
                        {mediaItems.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {mediaItems.map((media, mi) => (
                              <Box
                                key={mi}
                                onClick={() => setLightbox({ open: true, url: media.url, type: media.type })}
                                sx={{
                                  width: 72, height: 72, borderRadius: 1.5, overflow: 'hidden',
                                  cursor: 'pointer', border: '1px solid', borderColor: 'divider',
                                  bgcolor: media.type === 'video' ? '#1a1a2e' : 'grey.100',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  '&:hover': { opacity: 0.8 },
                                }}
                              >
                                {media.type === 'video' ? (
                                  <PlayCircleOutlineIcon sx={{ color: '#fff', fontSize: 28 }} />
                                ) : (
                                  <Box component="img" src={media.url} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>

          <Divider />

          {/* Add update form */}
          {!showResolveForm && (
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AddCircleOutlineIcon fontSize="small" /> Add Follow-up
              </Typography>

              <TextField
                label="Update note"
                placeholder="Describe progress, findings, or next steps…"
                multiline
                minRows={3}
                fullWidth
                value={note}
                onChange={(e) => setNote(e.target.value)}
                sx={{ mb: 1.5 }}
              />

              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
                  Change status (optional)
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={statusChange}
                  onChange={(_, v) => setStatusChange(v)}
                >
                  {(['in_progress', 'awaiting_verification'] as RemedialStatus[]).map((s) => (
                    <ToggleButton key={s} value={s} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                      {s === 'in_progress' ? <><BuildIcon sx={{ fontSize: 14, mr: 0.5 }} />In Progress</> :
                        <><HourglassEmptyIcon sx={{ fontSize: 14, mr: 0.5 }} />Awaiting Verification</>}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              {/* Media attachments */}
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
                  Attach photos / videos (max 2 min)
                </Typography>
                {mediaFiles.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {mediaFiles.map((m, i) => (
                      <Box key={i} sx={{ position: 'relative' }}>
                        <Box sx={{
                          width: 64, height: 64, borderRadius: 1.5, overflow: 'hidden',
                          border: '1px solid', borderColor: 'divider',
                          bgcolor: m.type === 'video' ? '#1a1a2e' : 'grey.100',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {m.uploading ? (
                            <CircularProgress size={20} />
                          ) : m.type === 'video' ? (
                            <PlayCircleOutlineIcon sx={{ color: '#fff', fontSize: 24 }} />
                          ) : (
                            <Box component="img" src={m.url || URL.createObjectURL(m.file)} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => setMediaFiles((prev) => prev.filter((_, j) => j !== i))}
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', color: 'error.main', p: 0.25 }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<PhotoLibraryIcon />}
                  sx={{ textTransform: 'none', borderStyle: 'dashed' }}
                >
                  Choose files
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*,video/*"
                    onChange={(e) => handleFileSelect(e.target.files, setMediaFiles, log.location_id)}
                  />
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                  onClick={handleSubmitUpdate}
                  disabled={submitting || !note.trim()}
                  sx={{ textTransform: 'none' }}
                >
                  {submitting ? 'Saving…' : 'Submit Update'}
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setShowResolveForm(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Mark Resolved
                </Button>
              </Box>
            </Box>
          )}

          {/* Resolve form */}
          {showResolveForm && (
            <Box sx={{ px: 3, py: 2, bgcolor: 'success.50' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.dark' }}>
                <CheckCircleIcon fontSize="small" color="success" /> Resolve Issue
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Record the resolution and mark this {locLabel.toLowerCase()} as safe. This closes the open issue.
              </Typography>

              <TextField
                label="Resolution note (optional)"
                placeholder="Describe what was fixed, verified, or actioned…"
                multiline
                minRows={3}
                fullWidth
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
                sx={{ mb: 1.5 }}
              />

              {/* Evidence media */}
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
                  Attach evidence photos / videos (optional)
                </Typography>
                {resolveMedia.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {resolveMedia.map((m, i) => (
                      <Box key={i} sx={{ position: 'relative' }}>
                        <Box sx={{
                          width: 64, height: 64, borderRadius: 1.5, overflow: 'hidden',
                          border: '1px solid', borderColor: 'divider',
                          bgcolor: m.type === 'video' ? '#1a1a2e' : 'grey.100',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {m.uploading ? <CircularProgress size={20} /> :
                            m.type === 'video' ? <PlayCircleOutlineIcon sx={{ color: '#fff', fontSize: 24 }} /> :
                            <Box component="img" src={m.url || URL.createObjectURL(m.file)} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => setResolveMedia((prev) => prev.filter((_, j) => j !== i))}
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', color: 'error.main', p: 0.25 }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<PhotoLibraryIcon />}
                  sx={{ textTransform: 'none', borderStyle: 'dashed' }}
                >
                  Choose files
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*,video/*"
                    onChange={(e) => handleFileSelect(e.target.files, setResolveMedia, log.location_id)}
                  />
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={resolving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                  onClick={handleResolve}
                  disabled={resolving}
                  sx={{ textTransform: 'none' }}
                >
                  {resolving ? 'Resolving…' : 'Confirm Resolved'}
                </Button>
                <Button variant="text" onClick={() => setShowResolveForm(false)} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={lightbox.open} onClose={() => setLightbox({ open: false, url: '', type: '' })} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'flex-end', py: 1 }}>
          <IconButton onClick={() => setLightbox({ open: false, url: '', type: '' })} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          {lightbox.type === 'video' ? (
            <Box component="video" src={lightbox.url} controls sx={{ maxWidth: '100%', maxHeight: '70vh' }} />
          ) : (
            <Box component="img" src={lightbox.url} sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────────

const LocationChecks: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { adminUiLabels } = useAdminUiLabels();
  const tabParam = searchParams.get('tab');
  const safetyCheckIdParam = searchParams.get('safety_check_id') || searchParams.get('focus');

  const [value, setValue] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return tabSlugToIndex(params.get('tab'), params.get('safety_check_id') || params.get('focus'));
  });
  const [refreshing, setRefreshing] = useState(false);
  const { useSite, loaded: terminologyLoaded } = useSiteTerminology();
  const [stats, setStats] = useState<{ completed: number; failed: number; loading: boolean }>({ completed: 0, failed: 0, loading: true });

  const [recentChecks, setRecentChecks] = useState<RecentCheck[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState<string | null>(null);

  const [unsafeLocations, setUnsafeLocations] = useState<LocationAuditLog[]>([]);
  const [unsafeLoading, setUnsafeLoading] = useState(false);
  const [unsafeError, setUnsafeError] = useState<string | null>(null);

  const [analysisDialog, setAnalysisDialog] = useState<{ open: boolean; title: string; text: string }>(
    { open: false, title: '', text: '' },
  );

  // Remedial updates dialog
  const [remedialDialog, setRemedialDialog] = useState<{ open: boolean; log: LocationAuditLog | null }>(
    { open: false, log: null },
  );

  useEffect(() => {
    const idx = tabSlugToIndex(tabParam, safetyCheckIdParam);
    setValue((prev) => (idx !== prev ? idx : prev));
  }, [tabParam, safetyCheckIdParam]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      const [completedRes, failedRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/check-responses?check_type=LOCATION&check_status=completed,FAIL,fail&limit=1&page=1`, { headers }),
        axios.get(`${API_BASE_URL}/check-responses?check_type=LOCATION&check_status=FAIL,fail&limit=1&page=1`, { headers }),
      ]);
      const completedTotal = completedRes.data?.pagination?.totalCount ?? 0;
      const failedTotal = failedRes.data?.pagination?.totalCount ?? 0;
      setStats({ completed: completedTotal, failed: failedTotal, loading: false });
    } catch { setStats((prev) => ({ ...prev, loading: false })); }
  }, []);

  const fetchAnalysisForCheck = async (safetyCheckId: string, token: string): Promise<string | null> => {
    try {
      const res = await axios.get(`${API_BASE_URL}/check-responses/analysis/${safetyCheckId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const analyses = res.data?.data;
      if (res.data?.success && Array.isArray(analyses) && analyses.length > 0) return analyses[0].analysis_text || null;
    } catch { /* still processing */ }
    return null;
  };

  const fetchRecentChecks = useCallback(async () => {
    try {
      setRecentLoading(true);
      setRecentError(null);
      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await axios.get(
        `${API_BASE_URL}/check-responses?check_type=LOCATION&check_status=completed,FAIL,fail&limit=20&page=1`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const groups = res.data?.data || [];
      const baseChecks: RecentCheck[] = groups.map((g: any) => ({
        safety_check_id: g.safety_check_id,
        check_name: g.check_name,
        location_name: g.location_name,
        safety_check_status: g.safety_check_status,
        assigned_to_name: g.assigned_to_name,
        completed_at: g.responses?.[0]?.created_at || null,
      }));

      const withAnalysis = await Promise.all(
        baseChecks.map(async (check) => {
          const analysisText = await fetchAnalysisForCheck(check.safety_check_id, token);
          return { ...check, analysis_full: analysisText, analysis_excerpt: analysisText ? getAnalysisExcerpt(analysisText) : null };
        }),
      );

      setRecentChecks(withAnalysis);
    } catch { setRecentError('Failed to load recent checks.'); setRecentChecks([]); }
    finally { setRecentLoading(false); }
  }, []);

  const fetchUnsafeLocations = useCallback(async () => {
    try {
      setUnsafeLoading(true);
      setUnsafeError(null);
      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await axios.get(`${API_BASE_URL}/locations/out-of-action-audit`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.success) {
        const seen = new Set<string>();
        const logs: LocationAuditLog[] = (res.data.data || [])
          .filter((log: LocationAuditLog) => log.action === 'marked_out_of_action' && log.is_out_of_action === 1)
          .sort((a: LocationAuditLog, b: LocationAuditLog) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
          )
          .filter((log: LocationAuditLog) => {
            if (seen.has(log.location_id)) return false;
            seen.add(log.location_id);
            return true;
          });
        setUnsafeLocations(logs);
      } else { setUnsafeLocations([]); }
    } catch { setUnsafeError('Failed to load open issues.'); setUnsafeLocations([]); }
    finally { setUnsafeLoading(false); }
  }, []);

  const refreshOverview = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchStats(), fetchRecentChecks(), fetchUnsafeLocations()]);
    setRefreshing(false);
  }, [fetchStats, fetchRecentChecks, fetchUnsafeLocations]);

  useEffect(() => {
    fetchStats();
    fetchRecentChecks();
    fetchUnsafeLocations();
  }, [fetchStats, fetchRecentChecks, fetchUnsafeLocations]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    const params = new URLSearchParams(searchParams);
    if (newValue === 0) { params.delete('tab'); } else { params.set('tab', LOCATION_CHECK_TABS[newValue]); }
    setSearchParams(params, { replace: true });
  };

  const label = locationLabel(useSite);
  const pageTitle = resolveAdminUiLabel('nav_location_checks', adminUiLabels);
  const passed = Math.max(0, stats.completed - stats.failed);
  const recentPassed = recentChecks.filter((c) => !isFailedStatus(c.safety_check_status));
  const recentFailed = recentChecks.filter((c) => isFailedStatus(c.safety_check_status));

  const renderCheckTable = (checks: RecentCheck[], emptyMessage: string, showAnalysis: boolean) => {
    if (checks.length === 0) {
      return (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">{emptyMessage}</Typography>
        </Paper>
      );
    }
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Check</strong></TableCell>
              <TableCell><strong>{label}</strong></TableCell>
              <TableCell><strong>Completed by</strong></TableCell>
              {showAnalysis && <TableCell><strong>AI overview</strong></TableCell>}
              {showAnalysis && <TableCell><strong>Actions</strong></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {checks.map((check) => (
              <TableRow key={check.safety_check_id} hover>
                <TableCell>{check.completed_at ? new Date(check.completed_at).toLocaleString() : '—'}</TableCell>
                <TableCell><Typography variant="body2" fontWeight="medium">{check.check_name}</Typography></TableCell>
                <TableCell>{check.location_name || '—'}</TableCell>
                <TableCell>{check.assigned_to_name || '—'}</TableCell>
                {showAnalysis && (
                  <TableCell>
                    {check.analysis_excerpt ? (
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {check.analysis_excerpt}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">Analysis pending…</Typography>
                    )}
                  </TableCell>
                )}
                {showAnalysis && (
                  <TableCell>
                    {check.analysis_full && (
                      <Button size="small" variant="text" startIcon={<AssessmentIcon />} onClick={() => setAnalysisDialog({ open: true, title: `${check.check_name} — AI Analysis`, text: check.analysis_full || '' })} sx={{ textTransform: 'none' }}>
                        View full
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  if (!terminologyLoaded) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
      <PageHeader
        title={pageTitle}
        subtitle={`Risk assessments and other checks completed against your ${locationLabel(useSite, { plural: true, lowercase: true })}. Each completed check is AI-analysed and a PDF report is saved automatically under ${locationLabel(useSite)} Files → Check reports → ${locationLabel(useSite)}.`}
        action={
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
            onClick={refreshOverview}
            disabled={refreshing}
            sx={primaryButtonSx}
          >
            {refreshing ? 'Refreshing…' : 'Refresh overview'}
          </Button>
        }
      />

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatisticsCard title="Completed Checks" count={stats.loading ? <CircularProgress size={24} /> : stats.completed} icon={<AssignmentIcon />} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatisticsCard title="Passed" count={stats.loading ? <CircularProgress size={24} /> : passed} icon={<CheckCircleIcon />} color="success" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatisticsCard title="Failed" count={stats.loading ? <CircularProgress size={24} /> : stats.failed} icon={<CancelIcon />} color="error" />
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%', borderRadius: 1.5, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
        <Tabs value={value} onChange={handleChange} aria-label="location checks tabs" variant="fullWidth" sx={pageTabsSx}>
          <Tab label="Overview" />
          <Tab label="Responses" />
        </Tabs>

        <TabPanel value={value} index={0}>
          <Box sx={{ p: 3 }}>
            {/* Open issues requiring remedial action */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <WarningIcon color="error" fontSize="small" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Open Issues</Typography>
                {unsafeLocations.length > 0 && (
                  <Chip label={unsafeLocations.length} size="small" color="error" />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {label}s flagged after a failed check. Track remedial progress with follow-ups, attach photos/videos, and mark as resolved when the issue is closed.
              </Typography>

              {unsafeLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
              ) : unsafeError ? (
                <Typography color="error">{unsafeError}</Typography>
              ) : unsafeLocations.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No {locationLabel(useSite, { plural: true, lowercase: true })} with open issues.
                  </Typography>
                </Paper>
              ) : (
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>{label}</strong></TableCell>
                        <TableCell><strong>Issue</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unsafeLocations.map((log) => {
                        const status = effectiveStatus(log);
                        return (
                          <TableRow key={log.audit_id} hover>
                            <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">{log.location_name || 'Unknown'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {log.note || 'Failed site check'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={STATUS_META[status].icon}
                                label={STATUS_META[status].label}
                                color={STATUS_META[status].color}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip title="View and add follow-up updates, attach evidence, and resolve">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<BuildIcon />}
                                  onClick={() => setRemedialDialog({ open: true, log })}
                                  sx={{ textTransform: 'none', mr: 1 }}
                                >
                                  Manage
                                </Button>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </Box>

            {/* Recent failed checks */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CancelIcon color="error" fontSize="small" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Recent failures</Typography>
                <Chip label={recentFailed.length} size="small" color="error" variant="outlined" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Latest failed {locationLabel(useSite, { plural: true, lowercase: true })} checks with AI analysis summary.
              </Typography>
              {recentLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
              ) : recentError ? (
                <Typography color="error">{recentError}</Typography>
              ) : renderCheckTable(recentFailed, 'No recent failed checks.', true)}
            </Box>

            {/* Recent passed checks */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Recent passes</Typography>
                <Chip label={recentPassed.length} size="small" color="success" variant="outlined" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Latest passed {locationLabel(useSite, { plural: true, lowercase: true })} checks.
              </Typography>
              {recentLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
              ) : recentError ? (
                <Typography color="error">{recentError}</Typography>
              ) : renderCheckTable(recentPassed, 'No recent passed checks.', false)}
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={1}>
          <CheckResponses
            extraQuery={{
              check_type: 'LOCATION',
              check_status: 'completed,FAIL,fail',
              ...(safetyCheckIdParam ? { safety_check_id: safetyCheckIdParam } : {}),
            }}
          />
        </TabPanel>
      </Paper>

      {/* Full AI analysis dialog */}
      <Dialog open={analysisDialog.open} onClose={() => setAnalysisDialog({ open: false, title: '', text: '' })} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {analysisDialog.title}
            <IconButton onClick={() => setAnalysisDialog({ open: false, title: '', text: '' })} sx={{ color: (theme) => theme.palette.grey[500] }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
            {analysisDialog.text}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalysisDialog({ open: false, title: '', text: '' })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Remedial updates dialog */}
      <RemedialUpdatesDialog
        open={remedialDialog.open}
        log={remedialDialog.log}
        locationLabel={label}
        onClose={() => setRemedialDialog({ open: false, log: null })}
        onResolved={() => { fetchUnsafeLocations(); fetchStats(); }}
      />
    </Box>
  );
};

export default LocationChecks;
